import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { InfoItem, ResponsiveService } from '@frk/eds-components';
import { LiteratureOrderHistoryDetailService } from './services/literature-order-history-detail.service';
import { Observable, of, Subject, combineLatest, from } from 'rxjs';
import { TableResponsiveService } from '@shared/table/table-responsive.service';
import { TranslateService } from '@shared/translate/translate.service';
import { CartHandlerService } from '@literature/services/cart-handler.service';
import { stringCompare } from '@utils/sort-utils';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { IUserProfile, LoginSource } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import {
  CartActionPerformed,
  LiteratureDocumentListingData,
  OrderHistoryDetail,
  PersonalisationPersonalData,
} from '@types';
import { LiteratureGlobalConfigService } from '@literature/services/literature-global-config.service';
import { Logger } from '@utils/logger';
import { PersonalisationAPIService } from '../../../services/personalisation-api.service';
import { GlobalId } from '../../../types/nominals.type';
import { StorageService } from '@services';
import { FIRM_TOKEN } from '@utils/app.constants';
const logger = Logger.getLogger('LiteratureOrderHistoryDetailComponent');

@Component({
  selector: 'ft-literature-order-history-detail',
  templateUrl: './literature-order-history-detail.component.html',
  styleUrls: ['./literature-order-history-detail.component.scss'],
  providers: [TableResponsiveService],
})
export class LiteratureOrderHistoryDetailComponent
  extends AbstractBaseComponent
  implements OnInit, OnDestroy {
  @ViewChild('tableResponsive', { static: false })
  public set table(table: ElementRef) {
    if (table) {
      this.tableResponsiveService?.init(table);
    }
  }
  profile$: Observable<IUserProfile>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  cards: InfoItem[][] = [];
  isHandheld$: Observable<boolean>;
  orderDetails$: Observable<OrderHistoryDetail>;
  isMobile$: Observable<boolean>;
  globalId: GlobalId;

  public isPrimericaUser = false;

  constructor(
    private literatureOrderHistoryDetailService: LiteratureOrderHistoryDetailService,
    protected responsiveService: ResponsiveService,
    protected tableResponsiveService: TableResponsiveService,
    protected changeDetectorRef: ChangeDetectorRef,
    protected translateService: TranslateService,
    protected cartHandlerService: CartHandlerService,
    private profileService: ProfileService,
    private literatureConfig: LiteratureGlobalConfigService,
    private personalisationService: PersonalisationAPIService,
    private storageService: StorageService
  ) {
    super();
    this.tableResponsiveService.cards$
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((cards) => {
        this.cards = cards;
        this.changeDetectorRef.detectChanges();
      });
  }

  ngOnInit(): void {
    const firmToken$ = from(this.storageService.retrieve(FIRM_TOKEN));

    this.setBreadcrumbItems();
    const config = this.component?.getModels()?.config;
    const literatureOrderNumber = config?.literatureOrderNumber;
    this.isHandheld$ = this.responsiveService.isHandheld$();
    this.isMobile$ = this.responsiveService.isMobile$();
    this.profile$ = this.literatureOrderHistoryDetailService.getProfileData$();

    combineLatest([
      this.personalisationService.getPersonalData$(),
      this.literatureOrderHistoryDetailService.getProfileData$(),
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(([personalData, profile]) => {
          if (profile?.isLoggedIn && personalData?.identifiers?.globalId) {
            profile.profileInfo.contactGlobalId =
              personalData.identifiers.globalId;
          }
          // Populate order details and handle errors
          return this.literatureOrderHistoryDetailService
            .populate(profile.profileInfo, literatureOrderNumber)
            .pipe(
              catchError((errorResponse) => {
                logger.error(
                  '[literatureOrderHistoryDetailData] Received error from backend request. Please try to reload this page or try later.' +
                    JSON.stringify(errorResponse) +
                    ' [error status]: ' +
                    errorResponse.status
                );
                return of({} as OrderHistoryDetail);
              })
            );
        })
      )
      .subscribe((orderDetails) => {
        this.orderDetails$ = of(orderDetails);
      });
    this.onSort();

    firmToken$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((firmToken: string) => {
        this.isPrimericaUser = firmToken === 'partner-primerica';
      });

    this.changeDetectorRef.detectChanges();
  }
  /**
   * User can re-order documents from order history
   * @param data OrderHistoryDetail
   */
  reOrderItem(data: OrderHistoryDetail): void {
    const litdata: LiteratureDocumentListingData[] = data.document.map(
      (item) => {
        const mappedItem: LiteratureDocumentListingData = {
          literatureData: {
            ...item,
            docId: item?.docId ? item?.docId : item.literatureCode,
          },
        };
        return mappedItem;
      }
    );
    this.cartHandlerService.addToCart(
      litdata,
      CartActionPerformed.ADDED_FROM_ORDER_HISTORY
    );
    this.literatureConfig.navigateToUrl(
      `${this.literatureConfig.navigationUrls?.litOrderCheckoutUrl}`
    );
  }

  ngOnDestroy(): void {
    this.tableResponsiveService?.onDestroy();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  /**
   * Sorts document based on fundName
   * @param data OrderHistoryDetail
   * @returns OrderHistoryDetail
   */
  sortData(data: OrderHistoryDetail): OrderHistoryDetail {
    const nameCompare = stringCompare;
    data.document = data.document.sort((current, next) => {
      return nameCompare(current.fundName, next.fundName, null, null, false);
    });
    return data;
  }

  onSort(): void {
    this.orderDetails$ = this.orderDetails$?.pipe(
      map((item) => this.sortData(item))
    );
  }

  removeAndGetLinkValue(item): void {
    this.tableResponsiveService.removeValue(item);
  }
}
